import { typedUrlForRoute } from "$root/components/PlatformRouter/RoutesDef";
import { PortfolioStudioTab } from "$root/pages/PortfoliosStudio/portfolio-studio-tabs";
import { unreachable } from "@mdotm/utils/runtime";
import { type AclEntityMinInfo, AclEntityMinInfoEntityTypeEnum } from "@eu.mdotm.api/sphere";
import { ActionText, AutoTooltip, Text, TooltipContent } from "@mdotm/shadcn/legacy/components";
import { splitAt } from "@mdotm/utils/collections";
import { ForEach } from "@mdotm/utils/react-extra";

const VISIBLE_COUNT = 3;

type EntityGroup = {
	label: string;
	entities: AclEntityMinInfo[];
};

function groupEntities(entities: AclEntityMinInfo[]): EntityGroup[] {
	return [
		{
			label: "Universe",
			entities: entities.filter(
				(e) =>
					e.entityType === AclEntityMinInfoEntityTypeEnum.Universe ||
					e.entityType === AclEntityMinInfoEntityTypeEnum.GroupUniverse,
			),
		},
		{
			label: "Reference",
			entities: entities.filter((e) => e.entityType === AclEntityMinInfoEntityTypeEnum.Benchmark),
		},
		{
			label: "Market View",
			entities: entities.filter((e) => e.entityType === AclEntityMinInfoEntityTypeEnum.MarketView),
		},
		{
			label: "Portfolios",
			entities: entities.filter((e) => e.entityType === AclEntityMinInfoEntityTypeEnum.Investment),
		},
	].filter((group) => group.entities.length > 0);
}

function forwardEntityUrl(acl: AclEntityMinInfo) {
	if (!acl.identifier) {
		return "";
	}

	switch (acl.entityType) {
		case "BENCHMARK":
			return typedUrlForRoute("LegacyBenchmark", { uuid: acl.identifier });
		case "GROUP_UNIVERSE":
			return typedUrlForRoute("UniverseWithGroupsDetails", { universeUuid: acl.identifier });
		case "INVESTMENT":
			return typedUrlForRoute("PortfolioDetails", { portfolioUid: acl.identifier });
		case "MARKET_VIEW":
			return typedUrlForRoute("PortfoliosStudio", { tab: PortfolioStudioTab.MarketViews }); // cannot discriminate
		case "REFERENCE":
			return typedUrlForRoute("ReferenceDetails", { uuid: acl.identifier });
		case "UNIVERSE":
			return typedUrlForRoute("UniverseDetails", { universeUuid: acl.identifier });
		case undefined:
			return "";
		default:
			unreachable(acl.entityType);
	}
}

type LinkedEntitiesParams = {
	linkedEntities: AclEntityMinInfo[];
};

export function LinkedEntities({ linkedEntities }: LinkedEntitiesParams): React.JSX.Element {
	const entities = groupEntities(linkedEntities);

	return (
		<div className="flex flex-col gap-1">
			<Text type="Body/XL/Bold" className="mb-2">
				Access to linked entities
			</Text>
			<Text type="Body/M/Book" className="mb-2">
				Sharing portfolios gives read-only access to any linked entity.
			</Text>
			{entities.map(({ label, entities }) => {
				const [shown, remaining] = splitAt(entities, VISIBLE_COUNT);

				return (
					<div key={label} className="flex items-baseline gap-1 text-sm">
						<span className="size-1 rounded-full bg-current shrink-0 self-center" />
						<Text type="Body/M/Bold">{label}:</Text>
						<span>
							{shown.map((entity, i) => (
								<Text type="Body/M/Book" key={entity.identifier}>
									{i >= 1 && ", "}
									{entity.name}
								</Text>
							))}
							{remaining.length > 0 && (
								<>
									{" "}
									<Text type="Body/M/Book">and</Text>{" "}
									<AutoTooltip
										trigger={({ innerRef }) => (
											<Text type="Body/M/Link" innerRef={innerRef}>
												{remaining.length} more
											</Text>
										)}
										position="top"
										hideArrow
									>
										<TooltipContent hideCloseButton>
											<ul className="pl-3">
												<ForEach collection={remaining}>
													{(acl) => (
														<ActionText
															as="li"
															className="list-disc"
															onClick={() => 	window.open(forwardEntityUrl(acl), "_blank")}
														>
															{acl.name ?? ""}
														</ActionText>
													)}
												</ForEach>
											</ul>
										</TooltipContent>
									</AutoTooltip>
								</>
							)}
						</span>
					</div>
				);
			})}
			<Text type="Body/M/Book" className="mt-2">
				To manage permissions for specific linked entities, visit their dedicated pages.
			</Text>
			<Text type="Body/M/Book">Removing access to portfolios does not affect permissions on linked entities.</Text>
		</div>
	);
}
